/**
 * useConceptTime
 * ==============
 *
 * Study time per lesson concept, for Mindspace. A screen says which concepts
 * are in view (`setVisible`: the text section being read, the highlight card
 * shown); the hook adds up the time each set stays in view while the page is
 * visible, and sends the totals when the student leaves the screen or
 * switches away (`POST /analytics/concept-time`). One request per stretch,
 * not one per scroll. Errors are swallowed: this never disturbs the lesson.
 */

import { useCallback, useEffect, useRef } from 'react';
import { activityApi } from '@/lib/api-client';

/** Shorter stays are glances, not study (the backend ignores them too). */
const MIN_SECONDS = 10;

export function useConceptTime(lessonId: string | undefined) {
  const visibleRef = useRef<string[]>([]);
  const sinceRef = useRef<number | null>(null);
  const totalsRef = useRef<Map<string, number>>(new Map());

  const settle = useCallback(() => {
    const since = sinceRef.current;
    const ids = visibleRef.current;
    if (since !== null && ids.length > 0) {
      const key = ids.join(',');
      const seconds = (Date.now() - since) / 1000;
      totalsRef.current.set(key, (totalsRef.current.get(key) ?? 0) + seconds);
    }
    sinceRef.current =
      document.visibilityState === 'hidden' ? null : Date.now();
  }, []);

  const flush = useCallback(() => {
    settle();
    const items = [...totalsRef.current.entries()]
      .map(([key, seconds]) => ({
        concept_ids: key.split(','),
        seconds: Math.round(seconds),
      }))
      .filter(item => item.seconds >= MIN_SECONDS);
    totalsRef.current.clear();
    if (!lessonId || items.length === 0) return;
    activityApi.creditConceptTime(lessonId, items).catch(() => {
      // Swallow - analytics must never disturb the lesson.
    });
  }, [lessonId, settle]);

  /** The concepts now in view ([] for none). */
  const setVisible = useCallback(
    (conceptIds: string[]) => {
      const next = [...new Set(conceptIds)].sort();
      if (next.join(',') === visibleRef.current.join(',')) return;
      settle();
      visibleRef.current = next;
    },
    [settle]
  );

  useEffect(() => {
    sinceRef.current = Date.now();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
      else sinceRef.current = Date.now();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('beforeunload', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', flush);
      flush();
    };
  }, [flush]);

  return setVisible;
}
